import { useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { ArrowLeft, XLg } from "react-bootstrap-icons";
import { ModalSteps } from "./ModalStep";
import Assign from "./Assign";
import { Review } from "./Review";
import "./Upload.css";

const AssignModal = ({
  files,
  onClose,
  api,
  defaultContract,
  contractFilesUploadTypes,
  handleDocumentUploadSubmit,
  customerCode,
}) => {
  const [step, setStep] = useState(1);
  const [assignments, setAssignments] = useState(
    files.map((f) => ({
      ...f,
      contract: defaultContract ?? null,
      docType: null,
    })),
  );
  const [submitting, setSubmitting] = useState(false);

  const assignedCount = assignments.filter(
    (f) => f.contract && f.docType,
  ).length;
  const allAssigned = assignedCount === assignments.length;

  const handleAssign = (fileId, contract) => {
    setAssignments((prev) =>
      prev.map((f) => (f.id === fileId ? { ...f, contract } : f)),
    );
  };

  const handleDocTypeChange = (fileId, docType) => {
    setAssignments((prev) =>
      prev.map((f) => (f.id === fileId ? { ...f, docType } : f)),
    );
  };

  const applyDocTypeToAll = (docType) => {
    setAssignments((prev) => prev.map((f) => ({ ...f, docType })));
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    const renamedAssignments = assignments.map((f) => {
      const ext = f.name.includes(".") ? "." + f.name.split(".").pop() : "";
      const baseName = f.name.includes(".")
        ? f.name.slice(0, f.name.lastIndexOf("."))
        : f.name;
      const suffix = [f.contract?.number, f.docType].filter(Boolean).join("_");
      const newName = suffix ? `${suffix}_${baseName}${ext}` : f.name;
      return { ...f, name: newName };
    });

    await handleDocumentUploadSubmit(renamedAssignments);
    setSubmitting(false);
  };

  return (
    <Modal show onHide={onClose} size="xl" centered backdrop="static">
      <Modal.Header className="upload-modal-header">
        <div className="upload-modal-header__meta">
          <div className="upload-modal-header__title">
            {step === 1 ? "Assign to contracts" : "Review & submit"}
          </div>
          <div className="upload-modal-header__subtitle">
            {assignments.length} file{assignments.length !== 1 ? "s" : ""} ·{" "}
            {assignedCount} assigned
          </div>
        </div>
        <ModalSteps step={step} />
        <button
          className="upload-modal-close"
          onClick={onClose}
          type="button"
          aria-label="Close"
        >
          <XLg size={14} />
        </button>
      </Modal.Header>

      <Modal.Body className="upload-modal-body">
        {step === 1 && (
          <Assign
            assignments={assignments}
            onAssign={handleAssign}
            onDocTypeChange={handleDocTypeChange}
            onApplyDocTypeToAll={applyDocTypeToAll}
            api={api}
            defaultContract={defaultContract}
            contractFilesUploadTypes={contractFilesUploadTypes}
            customerCode={customerCode}
          />
        )}
        {step === 2 && <Review assignments={assignments} />}
      </Modal.Body>

      <Modal.Footer className="upload-modal-footer">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={step === 1 ? onClose : () => setStep(1)}
          style={{ display: "flex", alignItems: "center", gap: "6px" }}
        >
          <ArrowLeft size={13} />
          {step === 1 ? "Cancel" : "Edit assignments"}
        </Button>

        {step === 1 ? (
          <Button
            variant="dark"
            size="sm"
            disabled={!allAssigned}
            onClick={() => setStep(2)}
          >
            Review submission →
          </Button>
        ) : (
          <Button
            variant="dark"
            size="sm"
            disabled={submitting}
            onClick={handleSubmit}
            style={{ minWidth: "150px" }}
          >
            {submitting ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  style={{ marginRight: "6px" }}
                />
                Uploading…
              </>
            ) : (
              `Submit ${assignments.length} files ↑`
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default AssignModal;
